/**
 * ═══════════════════════════════════════════════════════════════════════════
 * USE MAILBOX TWO-WAY SYNC HOOK
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * React hook for managing two-way sync between Dashboard and Sequencers
 * 
 * FEATURES:
 * - Manual sync trigger
 * - Real-time progress tracking
 * - Connection status monitoring
 * - Sync history
 * 
 * USAGE:
 *   const { performSync, isSync, progress, connections } = useMailboxTwoWaySync()
 *   await performSync('smartlead')
 */

import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { mailboxTwoWaySyncService } from '@/lib/mailbox-sync/mailbox-two-way-sync-service'
import { slackAlertService } from '@/lib/mailbox-sync/slack-alert-service'
import type {
  SequencerType,
  TwoWaySyncResult,
  TwoWaySyncProgress,
  MailboxSequencerConnection,
} from '@/lib/mailbox-sync/two-way-sync-types'
import { toast } from 'sonner'

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export function useMailboxTwoWaySync(sequencerType: SequencerType = 'smartlead') {
  const queryClient = useQueryClient()
  const [progress, setProgress] = useState<TwoWaySyncProgress | null>(null)
  const [abortController, setAbortController] = useState<AbortController | null>(null)

  // ═════════════════════════════════════════════════════════════════════════
  // FETCH CONNECTIONS
  // ═════════════════════════════════════════════════════════════════════════

  const { data: connections = [], isLoading: isLoadingConnections } = useQuery({
    queryKey: ['mailbox-sequencer-connections', sequencerType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mailbox_sequencer_connections' as any)
        .select('*, mailboxes(email)')
        .eq('sequencer_platform', sequencerType)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as (MailboxSequencerConnection & { mailboxes: { email: string } })[]
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // ═════════════════════════════════════════════════════════════════════════
  // FETCH RECENT ALERTS
  // ═════════════════════════════════════════════════════════════════════════

  const { data: recentAlerts = [] } = useQuery({
    queryKey: ['sync-alerts', sequencerType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sync_alerts' as any)
        .select('*')
        .eq('sequencer_platform', sequencerType)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return data
    },
    refetchInterval: 60000, // Refetch every minute
  })

  // ═════════════════════════════════════════════════════════════════════════
  // FETCH DISCREPANCIES
  // ═════════════════════════════════════════════════════════════════════════

  const { data: discrepancies = [] } = useQuery({
    queryKey: ['activity-discrepancies', sequencerType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mailbox_activity_discrepancies' as any)
        .select('*, mailboxes(email)')
        .eq('sequencer_platform', sequencerType)
        .neq('status', 'resolved')
        .order('severity', { ascending: false })
        .order('check_date', { ascending: false })
        .limit(50) // Reduced from 100 to 50 for faster queries on large table (130MB)

      if (error) {
        console.error('Error fetching discrepancies:', error)
        throw error
      }
      console.log(`📊 Fetched ${data?.length || 0} discrepancies`)
      return data
    },
    refetchInterval: 60000, // Changed from 30s to 60s to reduce database load
    staleTime: 30000, // Cache results for 30s to avoid unnecessary refetches
    retry: 1, // Reduce retry attempts on timeout to fail faster
  })

  // ═════════════════════════════════════════════════════════════════════════
  // PERFORM SYNC MUTATION
  // ═════════════════════════════════════════════════════════════════════════

  const syncMutation = useMutation({
    mutationFn: async (type: SequencerType) => {
      const controller = new AbortController()
      setAbortController(controller)

      const result = await mailboxTwoWaySyncService.performFullSync(
        type,
        setProgress,
        controller.signal
      )

      // Send summary to Slack
      if (result.success) {
        await slackAlertService.sendSyncSummary(
          type,
          result.mailboxes_synced,
          result.conflicts_detected,
          result.new_mailboxes_imported,
          result.discrepancies_found,
          result.duration_ms
        )
      } else {
        await slackAlertService.sendSyncFailureAlert(type, result.message)
      }

      return result
    },
    onSuccess: (result: TwoWaySyncResult) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['mailbox-sequencer-connections'] })
      queryClient.invalidateQueries({ queryKey: ['sync-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['activity-discrepancies'] })
      queryClient.invalidateQueries({ queryKey: ['mailboxes'] })

      // Show success toast
      toast.success('Sync completed', {
        description: result.message,
      })

      // Show warnings if there are issues
      if (result.conflicts_detected > 0) {
        toast.warning(`${result.conflicts_detected} conflicts detected`, {
          description: 'Dashboard values were applied. Check alerts for details.',
        })
      }

      if (result.new_mailboxes_imported > 0) {
        toast.info(`${result.new_mailboxes_imported} new mailboxes imported`, {
          description: 'Please review and complete their information.',
        })
      }

      if (result.discrepancies_found > 0) {
        toast.warning(`${result.discrepancies_found} activity discrepancies found`, {
          description: 'Please investigate the mismatches.',
        })
      }

      setProgress(null)
      setAbortController(null)
    },
    onError: (error: Error) => {
      toast.error('Sync failed', {
        description: error.message,
      })
      setProgress(null)
      setAbortController(null)
    },
  })

  // ═════════════════════════════════════════════════════════════════════════
  // CANCEL SYNC
  // ═════════════════════════════════════════════════════════════════════════

  const cancelSync = useCallback(() => {
    if (abortController) {
      abortController.abort()
      setAbortController(null)
      setProgress(null)
      toast.info('Sync cancelled')
    }
  }, [abortController])

  // ═════════════════════════════════════════════════════════════════════════
  // PERFORM SYNC
  // ═════════════════════════════════════════════════════════════════════════

  const performSync = useCallback(
    async (type: SequencerType = sequencerType) => {
      return syncMutation.mutateAsync(type)
    },
    [sequencerType, syncMutation]
  )

  // ═════════════════════════════════════════════════════════════════════════
  // PROCESS UNSENT ALERTS
  // ═════════════════════════════════════════════════════════════════════════

  const processAlerts = useCallback(async () => {
    try {
      await slackAlertService.processUnsentAlerts()
      queryClient.invalidateQueries({ queryKey: ['sync-alerts'] })
      toast.success('Alerts processed')
    } catch (error) {
      toast.error('Failed to process alerts')
    }
  }, [queryClient])

  // ═════════════════════════════════════════════════════════════════════════
  // RETURN
  // ═════════════════════════════════════════════════════════════════════════

  return {
    // Sync operations
    performSync,
    cancelSync,
    isSyncing: syncMutation.isPending,
    progress,
    
    // Data
    connections,
    recentAlerts,
    discrepancies,
    
    // Loading states
    isLoadingConnections,
    
    // Alert operations
    processAlerts,
    
    // Stats
    stats: {
      totalConnections: connections.length,
      syncedConnections: connections.filter(c => c.sync_status === 'synced').length,
      conflictConnections: connections.filter(c => c.sync_status === 'conflict').length,
      failedConnections: connections.filter(c => c.sync_status === 'failed').length,
      pendingAlerts: recentAlerts.filter(a => a.status === 'new').length,
      unresolvedDiscrepancies: discrepancies.length,
    },
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// GET CONNECTION FOR MAILBOX
// ═══════════════════════════════════════════════════════════════════════════

export function useMailboxConnection(mailboxId: string, sequencerType: SequencerType) {
  return useQuery({
    queryKey: ['mailbox-connection', mailboxId, sequencerType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mailbox_sequencer_connections')
        .select('*')
        .eq('mailbox_id', mailboxId)
        .eq('sequencer_platform', sequencerType)
        .maybeSingle()

      if (error) throw error
      return data as MailboxSequencerConnection | null
    },
  })
}

